import type { UsePwaUpdateReturn } from '@/app/pwa/types';
import { useToasts } from '@/common/composables/useToast';
import { ref } from 'vue';
import { toast } from 'vue-sonner';

/**
 * Reference to the update service worker function
 * This will be set by the registerSW function in main.ts
 */
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

/**
 * @author Omar Sánchez Martínez
 * @since 2025-11-19
 * @description Composable for managing Service Worker updates
 * Provides reactive state for update availability and methods for triggering updates
 * Integrates with vue-sonner for displaying update notifications
 *
 * @returns {UsePwaUpdateReturn} Object containing state refs and update methods
 *
 * @example
 * const { needRefresh, offlineReady, updateServiceWorker, showUpdatePrompt } = usePwaUpdate();
 *
 * watch(needRefresh, (refresh) => {
 *   if (refresh) {
 *     showUpdatePrompt();
 *   }
 * });
 */
export function usePwaUpdate(): UsePwaUpdateReturn {
  const { successToast } = useToasts();

  // Track whether a new service worker version is available
  const needRefresh = ref(false);

  // Track whether the app is ready to work offline
  const offlineReady = ref(false);

  /**
   * Activate new service worker and optionally reload the page
   * This function is called when the user clicks the update button
   *
   * @param reloadPage - Whether to reload the page after updating (default: true)
   * @throws Error if updateSW is not initialized
   */
  const updateServiceWorker = async (reloadPage = true): Promise<void> => {
    if (!updateSW) {
      console.error('Service Worker update function not initialized');
      throw new Error('Service Worker update function not initialized');
    }

    try {
      await updateSW(reloadPage);
      needRefresh.value = false;
      successToast('Actualización completada', 'La aplicación se ha actualizado correctamente');
    } catch (error) {
      console.error('Failed to update service worker:', error);
      throw error;
    }
  };

  /**
   * Display update prompt to user with action buttons
   * Uses vue-sonner toast with "Actualizar" and "Después" buttons
   * The "Actualizar" button triggers updateServiceWorker(true)
   * The "Después" button dismisses the toast
   */
  const showUpdatePrompt = (): void => {
    toast.success('Nueva versión disponible', {
      description: 'Hay una actualización lista para instalar',
      duration: Infinity,
      position: 'bottom-right',
      action: {
        label: 'Actualizar',
        onClick: () => {
          updateServiceWorker(true).catch(error => {
            console.error('Update failed:', error);
          });
        },
      },
      cancel: {
        label: 'Después',
        onClick: () => {
          toast.dismiss();
        },
      },
    });
  };

  /**
   * Set the update service worker function
   * This is called from main.ts when registering the service worker
   * Must be called before updateServiceWorker() can be used
   *
   * @param fn - The update service worker function from registerSW
   */
  const setUpdateSW = (fn: (reloadPage?: boolean) => Promise<void>): void => {
    updateSW = fn;
  };

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
    showUpdatePrompt,
    setUpdateSW,
  };
}

/**
 * Global instance of the PWA update composable
 * Used to share state across the application
 */
let globalPwaUpdateInstance: ReturnType<typeof usePwaUpdate> | null = null;

/**
 * Get or create the global PWA update composable instance
 * Ensures only one instance exists across the application
 *
 * @returns {UsePwaUpdateReturn} The global PWA update composable instance
 */
export function useGlobalPwaUpdate(): ReturnType<typeof usePwaUpdate> {
  if (!globalPwaUpdateInstance) {
    globalPwaUpdateInstance = usePwaUpdate();
  }
  return globalPwaUpdateInstance;
}
